import { useMemo } from 'react';
import { useFormState } from 'react-final-form';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { required } from 'helpers/validators';
import { italianDistricts } from '../../../constants';
import {
  DEFAULT_DOCUMENT_FLOW,
  DEFAULT_ORDER_SEND_FORMAT,
  DEFAULT_RECEPTION_FORMAT,
  DOCUMENT_FLOW_VALUES,
  isFormBollaElettronica,
  toFormDocumentFlow,
  toFormReceptionFormat,
  ORDER_SEND_FORMAT_VALUES,
  RECEPTION_FORMAT_VALUES
} from '../../../constants/supplierIntegration';
import { ISupplier } from 'interfaces';
import { IdentityField } from 'components/form/IdentityField';
import { useTranslation } from 'react-i18next';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import SectionTitle from 'components/common/SectionTitle';
import { CheckboxAdapter } from 'components/form/CheckboxAdapter';

interface SupplierFormParams {
  supplier: ISupplier;
  onSubmit: any;
  disableEdit?: boolean;
}

type SupplierIntegrationFormatFieldsProps = {
  supplier: ISupplier;
  disableEdit?: boolean;
  documentFlowOptions: { value: string; display_name: string }[];
  orderSendFormatOptions: { value: string; display_name: string }[];
  receptionFormatOptions: { value: string; display_name: string }[];
  resolveOptionInit: (
    options: { value: string; display_name: string }[],
    currentValue: string | undefined,
    defaultValue: string
  ) => { value: string; display_name: string };
};

function SupplierIntegrationFormatFields({
  supplier,
  disableEdit,
  documentFlowOptions,
  orderSendFormatOptions,
  receptionFormatOptions,
  resolveOptionInit
}: SupplierIntegrationFormatFieldsProps) {
  const { t } = useTranslation();
  const { values } = useFormState({ subscription: { values: true } });
  const documentFlow = values?.document_flow ?? toFormDocumentFlow(supplier.document_flow);
  const showFormatFields = isFormBollaElettronica(documentFlow);

  return (
    <>
      <div className="w-full md:w-1/3 p-2">
        <IdentityField
          name="document_flow"
          component={AutocompleteAdapter}
          initValue={resolveOptionInit(
            documentFlowOptions,
            toFormDocumentFlow(supplier.document_flow),
            DEFAULT_DOCUMENT_FLOW
          )}
          options={documentFlowOptions}
          optionValue="value"
          optionLabel="display_name"
          label={t('entities.supplier.document_flow')}
          autoSelect={false}
          disabled={disableEdit}
        />
      </div>
      {showFormatFields && (
        <>
          <div className="w-full md:w-1/3 p-2">
            <IdentityField
              name="order_send_format"
              component={AutocompleteAdapter}
              initValue={resolveOptionInit(
                orderSendFormatOptions,
                supplier.order_send_format,
                DEFAULT_ORDER_SEND_FORMAT
              )}
              options={orderSendFormatOptions}
              optionValue="value"
              optionLabel="display_name"
              label={t('entities.supplier.order_send_format')}
              autoSelect={false}
              disabled={disableEdit}
            />
          </div>
          <div className="w-full md:w-1/3 p-2">
            <IdentityField
              name="reception_format"
              component={AutocompleteAdapter}
              initValue={resolveOptionInit(
                receptionFormatOptions,
                toFormReceptionFormat(supplier.reception_format),
                DEFAULT_RECEPTION_FORMAT
              )}
              options={receptionFormatOptions}
              optionValue="value"
              optionLabel="display_name"
              label={t('entities.supplier.reception_format')}
              autoSelect={false}
              disabled={disableEdit}
            />
          </div>
        </>
      )}
    </>
  );
}

function SupplierForm(params: SupplierFormParams) {
  const { t } = useTranslation();

  const { supplier, onSubmit, disableEdit } = params;

  const documentFlowOptions = useMemo(
    () =>
      DOCUMENT_FLOW_VALUES.map((value) => ({
        value,
        display_name: t(`entities.supplier.document_flow_options.${value}`)
      })),
    [t]
  );

  const orderSendFormatOptions = useMemo(
    () =>
      ORDER_SEND_FORMAT_VALUES.map((value) => ({
        value,
        display_name: t(`entities.supplier.order_send_format_options.${value}`)
      })),
    [t]
  );

  const receptionFormatOptions = useMemo(
    () =>
      RECEPTION_FORMAT_VALUES.map((value) => ({
        value,
        display_name: t(`entities.supplier.reception_format_options.${value}`)
      })),
    [t]
  );

  const resolveOptionInit = (
    options: { value: string; display_name: string }[],
    currentValue: string | undefined,
    defaultValue: string
  ) => options.find((o) => o.value === (currentValue ?? defaultValue)) ?? options[0];
  return (
    <div>
      {supplier && (
        <ReactFinalForm
          onSubmit={disableEdit ? {} : onSubmit}
          edit={!!supplier.id}
          disableEdit={disableEdit}>
          <div className="flex flex-wrap">
            <div className="w-full p-2">
              <SectionTitle>{t('pages.supplier.new.registry')}</SectionTitle>
            </div>
            <div className="w-full md:w-1/3 p-2">
              <IdentityField
                name="code"
                initialValue={supplier.code}
                component={TextFieldAdapter}
                label={t('global.code')}
                placeholder={t('global.code')}
                type="text"
                validate={required}
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-1/2 p-2">
              <IdentityField
                name="company_name"
                initialValue={supplier.company_name}
                component={TextFieldAdapter}
                label={t('entities.supplier.company_name')}
                placeholder={t('entities.supplier.company_name')}
                type="text"
                validate={required}
                disabled={disableEdit}
              />
            </div>
            <div className="p-2 w-1/6 md:pl-2">
              <IdentityField
                name="active"
                component={CheckboxAdapter}
                initialValue={supplier.active}
                label={t('global.active')}
                type="checkbox"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-2/5 p-2">
              <IdentityField
                name="city"
                initialValue={supplier.city}
                component={TextFieldAdapter}
                label={t('entities.supplier.city')}
                placeholder={t('entities.supplier.city')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-2/5 p-2">
              <IdentityField
                name="district"
                component={AutocompleteAdapter}
                initValue={italianDistricts.find((s) => s.code === supplier.district)}
                options={italianDistricts}
                optionValue={'code'}
                placeholder={t('entities.supplier.district')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-1/5 p-2">
              <IdentityField
                name="zip_code"
                initialValue={supplier.zip_code}
                component={TextFieldAdapter}
                label={t('entities.supplier.zip_code')}
                placeholder={t('entities.supplier.zip_code')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-1/2 p-2">
              <IdentityField
                name="address"
                initialValue={supplier.address}
                component={TextFieldAdapter}
                label={t('entities.supplier.address')}
                placeholder={t('entities.supplier.address')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-1/2 p-2">
              <IdentityField
                name="address_2"
                initialValue={supplier.address_2}
                component={TextFieldAdapter}
                label={t('entities.supplier.address_2')}
                placeholder={t('entities.supplier.address_2')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-1/2 p-2">
              <IdentityField
                name="vat_number"
                initialValue={supplier.vat_number}
                component={TextFieldAdapter}
                label={t('entities.supplier.vat_number')}
                placeholder={t('entities.supplier.vat_number')}
                type="text"
                inputProps={{ pattern: '[0-9]{5,15}' }}
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-1/2 p-2">
              <IdentityField
                name="fiscal_code"
                initialValue={supplier.fiscal_code}
                component={TextFieldAdapter}
                label={t('entities.supplier.fiscal_code')}
                placeholder={t('entities.supplier.fiscal_code')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="p-2 w-full">
              <IdentityField
                name="notes"
                label={t(`global.notes`)}
                placeholder={t(`global.notes`)}
                initialValue={supplier.notes}
                component={TextFieldAdapter}
                disabled={disableEdit}
              />
            </div>
            <div className="w-full p-2">
              <SectionTitle>{t('pages.supplier.new.contact')}</SectionTitle>
            </div>
            <div className="w-full md:w-1/3 p-2">
              <IdentityField
                name="email"
                initialValue={supplier.email}
                component={TextFieldAdapter}
                label={t('entities.supplier.email')}
                placeholder={t('entities.supplier.email')}
                type="email"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-1/3 p-2">
              <IdentityField
                name="email_credit_note"
                initialValue={supplier.email_credit_note}
                component={TextFieldAdapter}
                label={t('entities.supplier.email_credit_note')}
                type="email"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-1/2 p-2">
              <IdentityField
                name="phone_number"
                initialValue={supplier.phone_number}
                component={TextFieldAdapter}
                label={t('entities.supplier.phone_number')}
                placeholder={t('entities.supplier.phone_number')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-1/2 p-2">
              <IdentityField
                name="fax_number"
                initialValue={supplier.fax_number}
                component={TextFieldAdapter}
                label={t('entities.supplier.fax_number')}
                placeholder={t('entities.supplier.fax_number')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full p-2">
              <SectionTitle>{t('pages.supplier.new.payment')}</SectionTitle>
            </div>
            <div className="w-full md:w-4/12 p-2">
              <IdentityField
                name="iban"
                initialValue={supplier.iban}
                component={TextFieldAdapter}
                label={t('entities.supplier.iban')}
                placeholder={t('entities.supplier.iban')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full md:w-8/12 p-2">
              <IdentityField
                name="payment_terms"
                initialValue={supplier.payment_terms}
                component={TextFieldAdapter}
                label={t('entities.supplier.payment_terms')}
                placeholder={t('entities.supplier.payment_terms')}
                type="text"
                disabled={disableEdit}
              />
            </div>
            <div className="w-full p-2">
              <SectionTitle>{t('pages.supplier.new.integration')}</SectionTitle>
            </div>
            <SupplierIntegrationFormatFields
              supplier={supplier}
              disableEdit={disableEdit}
              documentFlowOptions={documentFlowOptions}
              orderSendFormatOptions={orderSendFormatOptions}
              receptionFormatOptions={receptionFormatOptions}
              resolveOptionInit={resolveOptionInit}
            />
          </div>
        </ReactFinalForm>
      )}
    </div>
  );
}

export default SupplierForm;
